import { Grid } from '../../core/Grid.js';
import { BranchesGame, StemPosition } from './BranchesGame.js';
import { BranchesVortex } from './BranchesVortex.js';
import { Position, Direction, edgeAdjacentCells, edgeKey, parsePositionKey } from '../../core/GridGeometry.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS, colorWithAlpha } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { TweenScheduler, MOTION, staggerDelay } from '../../rendering/juice.js';

export interface BranchesRenderConfig extends Partial<RenderConfig> {
    connectedColor?: string;
    disconnectedColor?: string;
    segmentWidth?: number;
    stemColor?: string;
    distanceBadgeColor?: string;
    violationColor?: string;
    digitBackgroundBorder?: string;
}

const BRANCHES_DEFAULTS = {
    connectedColor: COLORS.teal,
    disconnectedColor: COLORS.amber,
    segmentWidth: 4,
    stemColor: COLORS.teal,
    distanceBadgeColor: COLORS.teal,
    violationColor: COLORS.red,
    digitBackgroundBorder: COLORS.borderGray
};

// ── Segment animation (docs/juice.md) ─────────────────────────────────
//
// A branch segment already carries a direction: its chevron points *away from
// the stem*, along the flow. So it needs no gesture to tell it which way to
// draw — it grows the way the network grows, outward. That is the piece
// explaining the mechanic, and it is why Branches doesn't use the
// gesture-overrides rule the neutral two-point pieces need.
//
// A disconnected segment has no flow and so no heading, and opens from its
// middle instead rather than claim one.

/** How a segment was drawn on its last real frame, so a removed one can finish. */
interface SegShape {
    connected: boolean;
    near: Position;
    far: Position;
    color: string;
}

interface SegTween {
    kind: 'enter' | 'exit';
    /** Clock time the tween begins — in the future while a stagger delay runs. */
    start: number;
    /** Exit only: how it looked when it went. */
    shape: SegShape | null;
}

export class BranchesRenderer extends GridRenderer {
    private branchesConfig: typeof BRANCHES_DEFAULTS;
    private gateMode: boolean = false;
    private hintRingsEnabled: boolean = true;

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private segTweens = new Map<string, SegTween>();
    private segState = new Set<string>();            // what the last frame drew
    private segShapes = new Map<string, SegShape>(); // and how, for removals
    private segsSynced: boolean = false;             // first frame seeds
    private stem: StemPosition | null = null;        // where the stem is now
    private stemEnter: number | null = null;         // it growing in there
    private stemExit: { stem: StemPosition; start: number } | null = null;
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: BranchesGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: BranchesRenderConfig = {}) {
        super(canvas, grid, config);
        this.branchesConfig = { ...BRANCHES_DEFAULTS, ...config };
        // The stem anchors to a rim edge, so the cursor reads as a rim marker there
        // rather than a crosshair floating on the boundary.
        this.keyboardFineRimMarkers = true;
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Toggle the teal hint chevrons on extendable edges (on by default). */
    setHintRingsEnabled(enabled: boolean): void {
        this.hintRingsEnabled = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every segment and the stem at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.segTweens.clear();
        this.stemEnter = null;
        this.stemExit = null;
    }

    render(game: BranchesGame): void {
        const grid = game.getGrid();

        this.lastGame = game;
        this.animating = false;
        this.syncSegmentTweens(game);
        this.syncStemTween(game.getStem());

        this.clear();
        this.drawGrid();

        // Vortex: gate mode highlights (behind everything)
        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        // 1. Draw stem and branches
        this.drawStem(game);
        this.drawBranches(game);

        // 1b. Hint chevrons pointing where the teal network can grow (newcomer aid)
        this.drawHintChevrons(game);

        // Vortex: active gates and gate links
        if (isVortexGame(game)) {
            this.drawActiveVortexGates(game);
            this.drawVortexGateDashedLines(game);
        }

        // 2. Draw digit cell backgrounds (square with thick gray border, teal fill if correct)
        this.drawDigitBackgrounds(grid, game);

        // 2b. Flow triangles: little arrowheads just outside each digit box marking
        //     the branch segments entering/leaving it, colored like the segment.
        this.drawFlowTriangles(game);

        // 3. Draw digits (always white)
        this.drawDigits(grid);

        // 4. Draw distance badges (only for valid paths from stem)
        this.drawDistanceBadges(game);

        // 5. Highlight violations
        this.highlightViolations(game);

        // 6. Amber dot at the tip of an in-progress build
        this.drawBuildTip(game);

        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    // ── Segment / stem animation (docs/juice.md) ──────────────────────

    /**
     * Diff the segments against the last frame and start a tween per change. One
     * antenna click can lay a whole run at once, so a multi-segment change fans out
     * from the stem end — the branch appears to grow outward rather than land whole.
     */
    private syncSegmentTweens(game: BranchesGame): void {
        const segments = game.getSegments();
        if (!this.tweens.isEnabled()) {
            this.segTweens.clear();
            this.segState = new Set(segments);
            return;
        }
        // First frame on this board: adopt it as-is, or opening a grid with a saved
        // solution would regrow the whole tree.
        if (!this.segsSynced) {
            this.segState = new Set(segments);
            this.segsSynced = true;
            return;
        }

        const added: string[] = [];
        const removed: string[] = [];
        for (const key of segments) if (!this.segState.has(key)) added.push(key);
        for (const key of this.segState) if (!segments.has(key)) removed.push(key);
        if (added.length === 0 && removed.length === 0) return;

        // Order by distance from the stem where the game knows it, so a run laid in
        // one click grows outward along itself.
        const rank = (key: string) => {
            const edge = this.grid.deserializeEdge(key);
            if (!edge) return 0;
            const cells = edgeAdjacentCells(edge);
            const ds = cells.map(c => game.getDistance(c)).filter((d): d is number => d !== null);
            if (ds.length > 0) return Math.min(...ds);
            return this.grid.size * this.grid.size + (cells[0]?.row ?? 0) * this.grid.size + (cells[0]?.col ?? 0);
        };
        const changed = [...added, ...removed].sort((a, b) => rank(a) - rank(b));

        const now = this.tweens.now();
        changed.forEach((key, i) => {
            const start = now + staggerDelay(i, changed.length);
            const entering = segments.has(key);
            this.segTweens.set(key, {
                kind: entering ? 'enter' : 'exit', start,
                shape: this.segShapes.get(key) ?? null
            });
        });

        this.segState = new Set(segments);
    }

    /** How much of a segment is drawn this frame. */
    private segExtent(tween: SegTween, now: number): { grown: number; done: boolean } {
        const { value, done } = this.tweens.tween(tween.start, MOTION[tween.kind], now);
        // The `exit` curve runs the *disappearance*, so invert it.
        return { grown: tween.kind === 'enter' ? value : 1 - value, done };
    }

    /**
     * Moving the stem is a placement, not a journey round the rim: it is pulled up
     * where it was as it is planted where it now is, the same as Line's start pin.
     */
    private syncStemTween(stem: StemPosition | null): void {
        const same = stem && this.stem &&
            stem.side === this.stem.side && stem.index === this.stem.index;
        if (same) return;

        if (!this.tweens.isEnabled()) {
            this.stemEnter = null;
            this.stemExit = null;
        } else {
            const now = this.tweens.now();
            this.stemExit = this.stem ? { stem: this.stem, start: now } : null;
            this.stemEnter = stem ? now : null;
        }
        this.stem = stem ? { ...stem } : null;
    }

    /** Small amber dot marking the current tip while building a branch. */
    private drawBuildTip(game: BranchesGame): void {
        const tip = game.getBuildTip();
        if (!tip) return;
        const c = this.getCellCenter(tip);
        this.ctx.fillStyle = COLORS.amber;
        this.ctx.beginPath();
        this.ctx.arc(c.x, c.y, 5, 0, Math.PI * 2);
        this.ctx.fill();
    }

    private drawStem(game: BranchesGame): void {
        const now = this.tweens.now();

        // The stem left behind, shrinking away where it was. It is gone from game
        // state, so its entry cell has to be worked out from the tween alone.
        if (this.stemExit) {
            const { value, done } = this.tweens.tween(this.stemExit.start, MOTION.exit, now);
            if (done) this.stemExit = null;
            else {
                this.animating = true;
                this.drawStemAt(this.stemExit.stem, this.stemEntryCellOf(this.stemExit.stem), 1 - value);
            }
        }

        const stem = game.getStem();
        if (!stem) return;
        const entryCell = game.getStemEntryCell();
        if (!entryCell) return;

        let grown = 1;
        if (this.stemEnter !== null) {
            const { value, done } = this.tweens.tween(this.stemEnter, MOTION.enter, now);
            if (done) this.stemEnter = null;
            else {
                this.animating = true;
                grown = value;
            }
        }
        this.drawStemAt(stem, entryCell, grown);
    }

    /** The cell a stem on this rim edge feeds into. */
    private stemEntryCellOf(stem: StemPosition): Position {
        switch (stem.side) {
            case 'top': return { row: 0, col: stem.index };
            case 'bottom': return { row: this.grid.size - 1, col: stem.index };
            case 'left': return { row: stem.index, col: 0 };
            case 'right': return { row: stem.index, col: this.grid.size - 1 };
        }
    }

    /** The stem: its rim marker, and the chevron flowing from the rim into the
     *  first cell. `grown` scales the marker and extends the chevron inward. */
    private drawStemAt(stem: StemPosition, entryCell: Position, grown: number): void {
        if (grown <= 0.01) return;

        const { padding, cellSize } = this.config;
        const { stemColor } = this.branchesConfig;
        const cellCenter = this.getCellCenter(entryCell);

        // Calculate edge point based on stem side
        let edgeX: number, edgeY: number;
        switch (stem.side) {
            case 'top':
                edgeX = cellCenter.x;
                edgeY = padding;
                break;
            case 'bottom':
                edgeX = cellCenter.x;
                edgeY = padding + this.grid.size * cellSize;
                break;
            case 'left':
                edgeX = padding;
                edgeY = cellCenter.y;
                break;
            case 'right':
                edgeX = padding + this.grid.size * cellSize;
                edgeY = cellCenter.y;
                break;
        }

        // Entry segment: a chevron flowing from the rim into the first cell, so it
        // matches the rest of the network instead of a bare line.
        this.drawChevron({ x: edgeX, y: edgeY }, cellCenter, stemColor, grown);

        // Draw stem indicator (triangle) on the rim, scaled about the rim point so
        // it grows out of the edge rather than drifting along it.
        this.ctx.save();
        this.ctx.translate(edgeX, edgeY);
        this.ctx.scale(grown, grown);
        this.ctx.translate(-edgeX, -edgeY);
        this.drawStemIndicator(stem);
        this.ctx.restore();
    }

    private drawStemIndicator(stem: StemPosition): void {
        const { stemColor } = this.branchesConfig;

        // Convert stem position to cell position for drawRimMarker
        let row: number, col: number;
        let edge: 'top' | 'bottom' | 'left' | 'right';
        switch (stem.side) {
            case 'top':
                row = 0;
                col = stem.index;
                edge = 'top';
                break;
            case 'bottom':
                row = this.grid.size - 1;
                col = stem.index;
                edge = 'bottom';
                break;
            case 'left':
                row = stem.index;
                col = 0;
                edge = 'left';
                break;
            case 'right':
                row = stem.index;
                col = this.grid.size - 1;
                edge = 'right';
                break;
        }

        this.drawRimMarker({
            position: { row, col },
            color: stemColor,
            entry: true,
            edge
        });
    }

    private drawBranches(game: BranchesGame): void {
        const connectedSegments = game.getConnectedSegments();
        const disconnectedSegments = game.getDisconnectedSegments();
        const { connectedColor, disconnectedColor, violationColor } = this.branchesConfig;
        const now = this.tweens.now();

        // Use red for disconnected segments when all digits are in order
        const correctDigits = game.getCorrectDigits();
        const digitCount = game.getGrid().maxDigit - game.getGrid().minDigit + 1;
        const allDigitsCorrect = correctDigits.length === digitCount;

        /** How far along a segment is this frame, remembering how it was drawn. */
        const extentOf = (seg: string, shape: SegShape): number => {
            this.segShapes.set(seg, shape);
            const tween = this.segTweens.get(seg);
            if (!tween) return 1;
            const { grown, done } = this.segExtent(tween, now);
            if (done) {
                this.segTweens.delete(seg);
                return 1;
            }
            this.animating = true;
            return grown;
        };

        // Disconnected segments: short rounded dashes (orange, or red when all in order).
        const discColor = allDigitsCorrect ? violationColor : disconnectedColor;
        for (const seg of disconnectedSegments) {
            const cells = this.segmentCells(seg);
            if (!cells) continue;
            const grown = extentOf(seg, { connected: false, near: cells[0], far: cells[1], color: discColor });
            this.drawDisconnectedSegment(cells, discColor, grown);
        }

        // Connected segments: teal chevrons pointing away from the stem, with a
        // notch at the near end so consecutive segments read as separate clicks.
        for (const seg of connectedSegments) {
            const ends = this.connectedEnds(game, seg);
            if (!ends) continue;
            const grown = extentOf(seg, { connected: true, near: ends[0], far: ends[1], color: connectedColor });
            this.drawChevron(this.getCellCenter(ends[0]), this.getCellCenter(ends[1]), connectedColor, grown);
        }

        // Segments being removed are already gone from game state — they only exist
        // here, drawn the way they were on their last real frame.
        for (const [seg, tween] of this.segTweens) {
            if (tween.kind !== 'exit' || !tween.shape) continue;
            if (connectedSegments.has(seg) || disconnectedSegments.has(seg)) continue;
            const { grown, done } = this.segExtent(tween, now);
            if (done) {
                this.segTweens.delete(seg);
                this.segShapes.delete(seg);
                continue;
            }
            this.animating = true;
            const { connected, near, far, color } = tween.shape;
            if (connected) this.drawChevron(this.getCellCenter(near), this.getCellCenter(far), color, grown);
            else this.drawDisconnectedSegment([near, far], color, grown);
        }
    }

    /** The two cells a segment joins, or null if the key doesn't resolve. */
    private segmentCells(segKey: string): [Position, Position] | null {
        const edge = this.grid.deserializeEdge(segKey);
        if (!edge) return null;
        const cells = edgeAdjacentCells(edge);
        return cells.length === 2 ? [cells[0], cells[1]] : null;
    }

    /** A connected segment's cells, ordered near→far by distance from the stem —
     *  the flow direction, which is also the way its chevron points. */
    private connectedEnds(game: BranchesGame, segKey: string): [Position, Position] | null {
        const cells = this.segmentCells(segKey);
        if (!cells) return null;
        const [a, b] = cells;
        const dA = game.getDistance(a);
        const dB = game.getDistance(b);
        return (dA !== null && dB !== null && dB < dA) ? [b, a] : [a, b];
    }

    /**
     * A disconnected segment: a plain rounded stroke, inset a touch at each end.
     * It carries no flow, so `grown` opens it from the middle — there is no
     * direction for it to grow along.
     */
    private drawDisconnectedSegment(cells: [Position, Position], color: string, grown: number = 1): void {
        if (grown <= 0) return;

        const a = this.getCellCenter(cells[0]);
        const b = this.getCellCenter(cells[1]);
        const dx = b.x - a.x, dy = b.y - a.y;
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len, uy = dy / len;
        const inset = len * 0.14;

        // Open from the middle: half the span each way, so it has no false heading.
        const half = (len / 2 - inset) * grown;
        const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;

        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = this.branchesConfig.segmentWidth;
        this.ctx.lineCap = 'round';
        this.ctx.beginPath();
        this.ctx.moveTo(mx - ux * half, my - uy * half);
        this.ctx.lineTo(mx + ux * half, my + uy * half);
        this.ctx.stroke();
    }

    /**
     * A filled chevron from `near` to `far`: a right-angle tip at the far end and
     * a matching notch at the near end, with a small gap at each end so a chain of
     * them reads as separate clicks. The arrow shape and gaps help newcomers see
     * segments live *on the edges between cells*, not on the cells themselves.
     */
    private drawChevron(
        near: { x: number; y: number }, far: { x: number; y: number },
        color: string, grown: number = 1
    ): void {
        const dx = far.x - near.x, dy = far.y - near.y;
        const full = Math.hypot(dx, dy) || 1;
        const ux = dx / full, uy = dy / full;      // axis, near→far
        const px = -uy, py = ux;                   // perpendicular
        const t = this.branchesConfig.segmentWidth / 2;

        // Growing shortens the axis, so the arrow keeps its shape the whole way and
        // simply reaches further — it extends the way the flow runs, away from the
        // stem. Below the length of its own tip and notch there is no chevron to
        // make, so the first instant is silent rather than malformed.
        const len = full * grown;
        if (len < 2 * t + 2) return;

        const gap = len * 0.03;                     // small gap at each end
        const tip = t;                              // tip length == t ⇒ right-angle apex
        const notch = t;                            // matching notch depth
        const backBase = gap;                       // notch side corners
        const notchApex = gap + notch;              // concave apex (points forward)
        const frontBase = len - gap - tip;          // tip base
        const tipApex = len - gap;                  // tip apex

        const pt = (along: number, side: number) => ({
            x: near.x + ux * along + px * side,
            y: near.y + uy * along + py * side,
        });

        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        let p = pt(notchApex, 0); this.ctx.moveTo(p.x, p.y);  // notch apex (near, on axis)
        p = pt(backBase, t);  this.ctx.lineTo(p.x, p.y);       // back-right corner
        p = pt(frontBase, t); this.ctx.lineTo(p.x, p.y);       // tip base, right
        p = pt(tipApex, 0);   this.ctx.lineTo(p.x, p.y);       // tip apex (far, on axis)
        p = pt(frontBase, -t); this.ctx.lineTo(p.x, p.y);      // tip base, left
        p = pt(backBase, -t);  this.ctx.lineTo(p.x, p.y);      // back-left corner
        this.ctx.closePath();
        this.ctx.fill();
    }

    /** Small teal rings marking the next places to grow the network. */
    private drawHintChevrons(game: BranchesGame): void {
        if (!this.hintRingsEnabled) return;
        if (game.checkWin()) return;  // nothing more to hint once solved

        this.ctx.strokeStyle = colorWithAlpha('teal', 0.6);
        this.ctx.lineWidth = 2;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';
        this.ctx.setLineDash([]);

        // No stem yet: inward chevrons at the rim edges where the stem can start.
        if (!game.getStem()) {
            for (const stem of game.getStemHintPositions()) {
                const p = this.rimEdgePoint(stem);
                const { angle, ox, oy } = this.stemInwardChevron(stem, 7);
                this.drawHintChevron(p.x + ox, p.y + oy, angle, 5);
            }
            return;
        }

        // Otherwise: chevrons pointing along each move that would grow the network.
        for (const { from, to } of game.getHintMoves()) {
            const a = this.getCellCenter(from);
            const b = this.getCellCenter(to);
            this.drawHintChevron((a.x + b.x) / 2, (a.y + b.y) / 2, this.cellPointAngle(from, to), 6);
        }
    }

    /** Stroke a small chevron ("^") centred at (x,y), apex pointing along `angle`
     *  (0 = up/N, measured clockwise). */
    private drawHintChevron(x: number, y: number, angle: number, size: number): void {
        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);
        this.ctx.beginPath();
        this.ctx.moveTo(-size, size * 0.45);
        this.ctx.lineTo(0, -size * 0.55);
        this.ctx.lineTo(size, size * 0.45);
        this.ctx.stroke();
        this.ctx.restore();
    }

    /** Clockwise chevron rotation so the apex points from cell `from` toward `to`. */
    private cellPointAngle(from: Position, to: Position): number {
        if (to.row < from.row) return 0;               // N (up)
        if (to.row > from.row) return Math.PI;         // S (down)
        if (to.col > from.col) return Math.PI / 2;     // E (right)
        return -Math.PI / 2;                           // W (left)
    }

    /** Chevron angle (apex pointing into the grid) and inward pixel nudge for a
     *  stem rim position, so the chevron sits just off the rim. */
    private stemInwardChevron(stem: StemPosition, d: number): { angle: number; ox: number; oy: number } {
        switch (stem.side) {
            case 'top': return { angle: Math.PI, ox: 0, oy: d };       // point down (S)
            case 'bottom': return { angle: 0, ox: 0, oy: -d };         // point up (N)
            case 'left': return { angle: Math.PI / 2, ox: d, oy: 0 };  // point right (E)
            case 'right': return { angle: -Math.PI / 2, ox: -d, oy: 0 }; // point left (W)
        }
    }

    /** The point on the grid rim at the centre of a stem position's outer edge. */
    private rimEdgePoint(stem: StemPosition): { x: number; y: number } {
        const { padding, cellSize } = this.config;
        const size = this.grid.size;
        let cell: Position;
        switch (stem.side) {
            case 'top': cell = { row: 0, col: stem.index }; break;
            case 'bottom': cell = { row: size - 1, col: stem.index }; break;
            case 'left': cell = { row: stem.index, col: 0 }; break;
            case 'right': cell = { row: stem.index, col: size - 1 }; break;
        }
        const c = this.getCellCenter(cell);
        switch (stem.side) {
            case 'top': return { x: c.x, y: padding };
            case 'bottom': return { x: c.x, y: padding + size * cellSize };
            case 'left': return { x: padding, y: c.y };
            case 'right': return { x: padding + size * cellSize, y: c.y };
        }
    }

    private drawDigitBackgrounds(grid: Grid, game: BranchesGame): void {
        const { cellSize, backgroundColor } = this.config;
        const { digitBackgroundBorder, connectedColor } = this.branchesConfig;
        const positions = grid.getAllDigitPositions();
        const correctDigits = new Set(game.getCorrectDigits());
        const anchor = game.getBuildAnchor();

        for (const [digit, pos] of positions) {
            const topLeft = this.getCellTopLeft(pos);
            const isCorrect = correctDigits.has(digit);
            const isAnchor = anchor !== null && anchor.row === pos.row && anchor.col === pos.col;

            // Fill: amber wash while this digit is the branch-build anchor.
            this.ctx.fillStyle = isAnchor ? colorWithAlpha('amber', 0.3) : backgroundColor;
            this.ctx.fillRect(topLeft.x + 4, topLeft.y + 4, cellSize - 8, cellSize - 8);

            // Border: amber when building from here, else teal if correct / gray.
            const borderColor = isAnchor
                ? COLORS.amber
                : (isCorrect ? connectedColor : digitBackgroundBorder);
            this.drawCellBorder(pos, borderColor, 4);
        }
    }

    /**
     * Draw small arrowheads just outside each digit box, one per incident branch
     * segment, in that segment's colour. An arrowhead points *into* the box when
     * flow arrives from that side (the neighbour is closer to the stem) and *out*
     * otherwise — mirroring the entry/exit triangles used in Mirrors and others.
     */
    private drawFlowTriangles(game: BranchesGame): void {
        const grid = game.getGrid();
        const connected = game.getConnectedSegments();
        const { connectedColor } = this.branchesConfig;

        const dirs: Direction[] = ['N', 'S', 'E', 'W'];

        for (const [, pos] of grid.getAllDigitPositions()) {
            const thisDist = game.getDistance(pos);
            for (const dir of dirs) {
                const neighbor = grid.geometry.moveInside(pos, dir);
                if (!neighbor) continue;
                const edge = grid.geometry.createEdgeFromCells(pos, neighbor);
                if (!edge) continue;

                // Only stem-connected segments carry flow — disconnected (amber)
                // ones have nothing flowing, so they get no arrow.
                if (!connected.has(edgeKey(edge))) continue;

                // Inbound when this digit is reached *through* the neighbour, i.e.
                // the neighbour sits one step closer to the stem.
                const neighborDist = game.getDistance(neighbor);
                const inbound = thisDist !== null && neighborDist !== null &&
                    neighborDist < thisDist;

                this.drawFlowTriangle(pos, dir, connectedColor, inbound);
            }
        }
    }

    /** One flow arrowhead, anchored on the digit box border on `dir`'s side. */
    private drawFlowTriangle(pos: Position, dir: Direction, color: string, inbound: boolean): void {
        const { cellSize } = this.config;
        const topLeft = this.getCellTopLeft(pos);
        const center = this.getCellCenter(pos);
        const boxInset = 4;   // matches the digit box border
        const w = 6;          // half base
        const h = 8;          // height

        // Anchor (on the box border, centred on the segment) and the canonical
        // "south" triangle's rotation so that local +y points outward.
        let x: number, y: number, angle: number;
        switch (dir) {
            case 'S': x = center.x; y = topLeft.y + cellSize - boxInset; angle = 0; break;
            case 'N': x = center.x; y = topLeft.y + boxInset; angle = Math.PI; break;
            case 'E': x = topLeft.x + cellSize - boxInset; y = center.y; angle = -Math.PI / 2; break;
            case 'W': x = topLeft.x + boxInset; y = center.y; angle = Math.PI / 2; break;
        }

        this.ctx.save();
        this.ctx.translate(x, y);
        this.ctx.rotate(angle);
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        if (inbound) {
            // Base on the box border, apex pointing inward — sits inside the box.
            this.ctx.moveTo(-w, 0);
            this.ctx.lineTo(w, 0);
            this.ctx.lineTo(0, -h);
        } else {
            // Base flush against the box border, apex pointing outward.
            this.ctx.moveTo(-w, 0);
            this.ctx.lineTo(w, 0);
            this.ctx.lineTo(0, h);
        }
        this.ctx.closePath();
        this.ctx.fill();
        this.ctx.restore();
    }

    private drawDistanceBadges(game: BranchesGame): void {
        const grid = game.getGrid();
        const correctSet = new Set(game.getCorrectDigits());

        // Build dangling endpoint set
        const danglingSet = new Set(
            game.getDanglingEndpoints().map(p => `${p.row},${p.col}`)
        );

        const badgePositions = new Map<string, { pos: Position; distance: number; color: string }>();

        // Non-digit branch tips: teal normally, red when dangling (a stray tip left
        // over once all digits are ordered). Digit tips fall to the digit loop below.
        for (const [pos, distance] of game.getEndpoints()) {
            if (grid.getDigitAt(pos) !== null) continue;
            const key = `${pos.row},${pos.col}`;
            const color = danglingSet.has(key)
                ? this.branchesConfig.violationColor
                : this.branchesConfig.distanceBadgeColor;
            badgePositions.set(key, { pos, distance, color });
        }

        // Every digit reached from the stem shows its distance — covering correct
        // digits, mid-path digits, and digit tips alike. Color reflects order: teal
        // when correct, orange when out of order.
        for (const [digit, pos] of grid.getAllDigitPositions()) {
            const distance = game.getDistance(pos);
            if (distance === null) continue;
            const color = correctSet.has(digit)
                ? this.branchesConfig.distanceBadgeColor
                : this.branchesConfig.disconnectedColor;
            badgePositions.set(`${pos.row},${pos.col}`, { pos, distance, color });
        }

        // Add active gate cells with distances (both gates of a pair show badge together)
        if (game instanceof BranchesVortex) {
            for (const [key] of game.getActiveGatePairs()) {
                if (badgePositions.has(key)) continue;
                const pos = parsePositionKey(key);
                const distance = game.getDistance(pos);
                if (distance !== null) {
                    badgePositions.set(key, { pos, distance, color: this.branchesConfig.distanceBadgeColor });
                }
            }
        }

        // Draw all badges
        for (const { pos, distance, color } of badgePositions.values()) {
            this.drawBadge(pos, 'NE', distance.toString(), color, 'sm', true);
        }
    }

    private highlightViolations(game: BranchesGame): void {
        const violations = game.getBranchingViolations();
        const { violationColor } = this.branchesConfig;

        for (const cellKey of violations) {
            const pos = parsePositionKey(cellKey);
            this.drawCellBorder(pos, violationColor);
        }
    }

}
